<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useForm, usePage, router, Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

// Get CSRF token from meta tag
const getCsrfToken = () => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
};

// Configure axios to use CSRF token
axios.defaults.headers.common['X-CSRF-TOKEN'] = getCsrfToken();

const props = defineProps({
    features: {
        type: Array,
        default: () => []
    }
});

const page = usePage();

// State
const featureList = ref([...props.features]);
const isEditing = ref(false);
const editingId = ref(null);
const wordCount = ref(0);
const originalData = ref(null);
const togglingId = ref(null); // ID of item being toggled

// Modal states for notifications
const errorModalContent = ref([]);
const modalType = ref('error'); // 'error', 'warning', 'success', 'info'

// Content view modal
const viewModalTitle = ref('');
const viewModalContent = ref('');
const viewModalItem = ref({});

// CKEditor instances
let descEditor = null;
let artikelEditor = null;

const form = useForm({
    feature_icon: '',
    feature_title: '',
    feature_subtitle: '',
    feature_slug: '',
    feature_description: '',
    feature_artikel: '',
    feature_active: 1,
    meta_title: '',
    meta_description: ''
});

// Slugify function
const slugify = (text) => {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

// Show notification modal
const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    nextTick(() => {
        try {
            const modalEl = document.getElementById('notificationModal');
            if (modalEl && typeof bootstrap !== 'undefined') {
                const modal = new bootstrap.Modal(modalEl);
                modal.show();
            } else {
                alert(Array.isArray(messages) ? messages.join('\n') : messages);
            }
        } catch (e) {
            alert(Array.isArray(messages) ? messages.join('\n') : messages);
        }
    });
};

// Open Guide Modal
const openGuideModal = () => {
    const modal = new bootstrap.Modal(document.getElementById('featureGuideModal'));
    modal.show();
};

// View Description Modal
const openDescModal = (feature) => {
    viewModalItem.value = feature;
    viewModalTitle.value = `Deskripsi: ${feature.feature_title}`;
    viewModalContent.value = feature.feature_description || '';
    const modal = new bootstrap.Modal(document.getElementById('viewDescModal'));
    modal.show();
};

// View Artikel Modal
const openArtikelModal = (feature) => {
    viewModalItem.value = feature;
    viewModalTitle.value = `Artikel: ${feature.feature_title}`;
    viewModalContent.value = feature.feature_artikel || '';
    const modal = new bootstrap.Modal(document.getElementById('viewArtikelModal'));
    modal.show();
};

const updateWordCount = () => {
    const text = descEditor ? descEditor.getData().replace(/<[^>]*>/g, '') : '';
    wordCount.value = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
};

// Normalize HTML for comparison
const normalizeHtml = (html) => {
    if (!html) return '';
    return html.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

const initCKEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    // Destroy existing instances
    if (CKEDITOR.instances['feature_description']) CKEDITOR.instances['feature_description'].destroy();
    if (CKEDITOR.instances['feature_artikel']) CKEDITOR.instances['feature_artikel'].destroy();

    // Description editor (limited toolbar, max 500 words)
    descEditor = CKEDITOR.replace('feature_description', {
        height: 150,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        format_tags: 'p;h3;h4;h5;h6',
        warnOnUnload: false,
        dialog_noConfirmCancel: true
    });

    descEditor.on('change', () => {
        form.feature_description = descEditor.getData();
        updateWordCount();
    });

    // Artikel editor (full toolbar)
    artikelEditor = CKEDITOR.replace('feature_artikel', {
        height: 400,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', 'Blockquote'] },
            { name: 'styles', items: ['Format'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['HorizontalRule'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ],
        warnOnUnload: false,
        dialog_noConfirmCancel: true
    });

    artikelEditor.on('change', () => {
        form.feature_artikel = artikelEditor.getData();
    });
};

onMounted(() => {
    nextTick(() => {
        initCKEditor();
    });
    
    // Show flash messages if present
    if (page.props.flash?.error) {
        showModal('error', page.props.flash.error);
    }
    if (page.props.flash?.success) {
        showModal('success', page.props.flash.success);
    }
});

onBeforeUnmount(() => {
    if (typeof CKEDITOR !== 'undefined') {
        // Destroy instances safely to avoid editor-destroy-iframe error
        if (CKEDITOR.instances['feature_description']) {
            try { CKEDITOR.instances['feature_description'].destroy(); } catch(e) {}
        }
        if (CKEDITOR.instances['feature_artikel']) {
            try { CKEDITOR.instances['feature_artikel'].destroy(); } catch(e) {}
        }
    }
});

// Auto-generate Meta Title (max 55 chars)
const generateMetaTitle = (title) => {
    if (!title) return '';
    // Format: "[Feature Title] - Sewa Mobil Banyuwangi"
    const keyword = 'Sewa Mobil Banyuwangi';
    let metaTitle = `${title} - ${keyword}`;
    if (metaTitle.length > 55) {
        metaTitle = metaTitle.substring(0, 52) + '...';
    }
    return metaTitle;
};

// Auto-generate Meta Description (max 155 chars)
const generateMetaDescription = (title, subtitle) => {
    if (!title) return '';
    // Format: "[Subtitle]: [Title]. Layanan sewa mobil Banyuwangi terpercaya dengan armada berkualitas dan harga terbaik."
    const baseDesc = subtitle 
        ? `${subtitle}: ${title}. Layanan sewa mobil Banyuwangi terpercaya dengan armada berkualitas dan harga terbaik.`
        : `${title}. Layanan sewa mobil Banyuwangi terpercaya dengan armada berkualitas dan harga terbaik.`;
    
    if (baseDesc.length > 155) {
        return baseDesc.substring(0, 152) + '...';
    }
    return baseDesc;
};

// Watch for feature_title changes to auto-generate meta and slug (only when not editing)
watch(() => form.feature_title, (newValue) => {
    if (!isEditing.value && newValue) {
        form.feature_slug = slugify(newValue);
        form.meta_title = generateMetaTitle(newValue);
        form.meta_description = generateMetaDescription(newValue, form.feature_subtitle);
    }
});

// Watch for feature_subtitle changes to update meta_description
watch(() => form.feature_subtitle, (newValue) => {
    if (!isEditing.value && form.feature_title) {
        form.meta_description = generateMetaDescription(form.feature_title, newValue);
    }
});

// Watch for prop changes
watch(() => props.features, (newFeatures) => {
    featureList.value = [...newFeatures];
}, { deep: true });

const resetForm = () => {
    form.reset();
    form.clearErrors();
    isEditing.value = false;
    editingId.value = null;
    originalData.value = null;
    wordCount.value = 0;
    if (descEditor) descEditor.setData('');
    if (artikelEditor) artikelEditor.setData('');
};

const editFeature = (item) => {
    isEditing.value = true;
    editingId.value = item.id;
    form.feature_icon = item.feature_icon || '';
    form.feature_title = item.feature_title || '';
    form.feature_subtitle = item.feature_subtitle || '';
    form.feature_slug = item.feature_slug || '';
    form.feature_description = item.feature_description || '';
    form.feature_artikel = item.feature_artikel || '';
    form.meta_title = item.meta_title || '';
    form.meta_description = item.meta_description || '';
    
    // Store original data for change detection
    originalData.value = {
        feature_icon: item.feature_icon || '',
        feature_title: item.feature_title || '',
        feature_subtitle: item.feature_subtitle || '',
        feature_slug: item.feature_slug || '',
        feature_description: normalizeHtml(item.feature_description || ''),
        feature_artikel: normalizeHtml(item.feature_artikel || ''),
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || ''
    };
    
    if (descEditor) {
        descEditor.setData(item.feature_description || '', function() {
            this.resetDirty();
        });
    }
    if (artikelEditor) {
        artikelEditor.setData(item.feature_artikel || '', function() {
            this.resetDirty();
        });
    }
    updateWordCount();
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const isDataChanged = () => {
    if (!originalData.value) return true;
    
    const currentData = {
        feature_icon: form.feature_icon,
        feature_title: form.feature_title,
        feature_subtitle: form.feature_subtitle,
        feature_slug: form.feature_slug,
        feature_description: normalizeHtml(form.feature_description),
        feature_artikel: normalizeHtml(form.feature_artikel),
        meta_title: form.meta_title,
        meta_description: form.meta_description
    };
    
    return JSON.stringify(originalData.value) !== JSON.stringify(currentData);
};

const submitForm = async () => {
    // Show button spinner first (like Carousel)
    form.processing = true;
    
    // Small delay to show spinner effect
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Sync CKEditor data
    if (descEditor) form.feature_description = descEditor.getData();
    if (artikelEditor) form.feature_artikel = artikelEditor.getData();

    // Client-side validation: check required fields
    const errors = [];
    if (!form.feature_title || form.feature_title.trim() === '') {
        errors.push('Judul feature wajib diisi.');
    }
    if (!form.feature_icon || form.feature_icon.trim() === '') {
        errors.push('Icon feature wajib diisi.');
    }
    
    if (errors.length > 0) {
        form.processing = false;
        showModal('error', errors);
        return;
    }

    // Word count validation (client-side)
    if (wordCount.value > 500) {
        form.processing = false;
        showModal('warning', `Deskripsi maksimal 500 kata. Saat ini: ${wordCount.value} kata.`);
        return;
    }

    // Check for changes in edit mode
    if (isEditing.value && !isDataChanged()) {
        form.processing = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }

    const options = {
        preserveScroll: true,
        onSuccess: () => {
            resetForm();
        },
        onError: (errors) => {
            errorModalContent.value = Object.values(errors).flat();
            modalType.value = 'error';
            nextTick(() => {
                try {
                    const modalEl = document.getElementById('notificationModal');
                    if (modalEl && typeof bootstrap !== 'undefined') {
                        const modal = new bootstrap.Modal(modalEl);
                        modal.show();
                    }
                } catch (e) {
                    alert(Object.values(errors).flat().join('\n'));
                }
            });
        }
    };

    if (isEditing.value) {
        // Use POST with _method: 'PUT' for Laravel
        form.transform((data) => ({
            ...data,
            _method: 'PUT'
        })).post(`/admin/feature/${editingId.value}`, options);
    } else {
        form.post('/admin/feature/store', options);
    }
};

const toggleStatus = async (item) => {
    if (togglingId.value) return; // Prevent double-click
    togglingId.value = item.id;
    try {
        const response = await axios.patch(`/admin/feature/${item.id}/toggle-status`);
        if (response.data.status === 'success') {
            item.feature_active = response.data.data.feature_active;
        }
    } catch (error) {
        // Silent fail, just revert UI
    } finally {
        togglingId.value = null;
    }
};

const refreshList = async () => {
    try {
        const response = await axios.get(route('admin.feature.get'));
        if (response.data.status === 'success') {
            featureList.value = response.data.data;
        }
    } catch (e) {
        console.error(e);
    }
};

</script>

<template>
    <Head title="Feature" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Feature</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Feature</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <!-- Form Section - Full Width -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-star me-2"></i>{{ isEditing ? 'Edit Data Feature' : 'Input Data Feature' }}
                            </h4>
                            <div class="box-controls pull-right">
                                <button type="button" @click="openGuideModal" class="btn btn-outline-info btn-sm">
                                    <i class="fa fa-question-circle me-1"></i>Panduan
                                </button>
                            </div>
                        </div>
                        <form @submit.prevent>
                            <div class="box-body">
                                <div class="row">
                                    <!-- Left Column: Icon, Title, Subtitle -->
                                    <div class="col-lg-6 col-12">
                                        <div class="form-group mb-3">
                                            <label for="feature_icon" class="form-label">Feature Icon (opsional)</label>
                                            <input type="text" id="feature_icon" name="feature_icon" v-model="form.feature_icon" class="form-control" placeholder="Contoh: fa fa-star">
                                            <small class="text-muted">Gunakan class FontAwesome, contoh: fa fa-star</small>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                            <label for="feature_title" class="form-label">Feature Title <span class="text-danger">*</span></label>
                                            <input type="text" id="feature_title" name="feature_title" v-model="form.feature_title" class="form-control" required placeholder="Masukkan title feature">
                                            <div class="text-danger small" v-if="form.errors.feature_title">{{ form.errors.feature_title }}</div>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                            <label for="feature_subtitle" class="form-label">Feature Subtitle <span class="text-danger">*</span></label>
                                            <input type="text" id="feature_subtitle" name="feature_subtitle" v-model="form.feature_subtitle" class="form-control" required placeholder="Masukkan subtitle feature">
                                            <div class="text-danger small" v-if="form.errors.feature_subtitle">{{ form.errors.feature_subtitle }}</div>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                            <label for="feature_slug" class="form-label">Slug</label>
                                            <input type="text" id="feature_slug" name="feature_slug" v-model="form.feature_slug" class="form-control" placeholder="slug-otomatis-dari-title">
                                            <small class="text-muted">Otomatis dibuat dari Title. Dapat diubah manual.</small>
                                            <div class="text-danger small" v-if="form.errors.feature_slug">{{ form.errors.feature_slug }}</div>
                                        </div>
                                    </div>
                                    
                                    <!-- Right Column: Meta SEO -->
                                    <div class="col-lg-6 col-12">
                                        <div class="card bg-light-light mb-0">
                                            <div class="card-body py-3">
                                                <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                                
                                                <div class="mb-3">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="meta_title" class="form-label">Meta Title</label>
                                                        <small :class="form.meta_title?.length > 55 ? 'text-danger' : 'text-muted'">{{ form.meta_title?.length || 0 }}/55</small>
                                                    </div>
                                                    <input type="text" id="meta_title" v-model="form.meta_title" class="form-control" maxlength="55" placeholder="Meta Title" autocomplete="off">
                                                    <small class="text-muted d-block mt-1">Judul yang akan muncul di hasil pencarian Google.</small>
                                                </div>
                                                <div class="mb-0">
                                                    <div class="d-flex justify-content-between">
                                                        <label for="meta_description" class="form-label">Meta Description</label>
                                                        <small :class="form.meta_description?.length > 155 ? 'text-danger' : 'text-muted'">{{ form.meta_description?.length || 0 }}/155</small>
                                                    </div>
                                                    <textarea id="meta_description" v-model="form.meta_description" class="form-control" rows="3" maxlength="155" placeholder="Meta Description" autocomplete="off"></textarea>
                                                    <small class="text-muted d-block mt-1">Deskripsi singkat yang muncul di bawah judul pada hasil pencarian.</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Row 2: Description | Artikel -->
                                <div class="row mt-3">
                                    <div class="col-lg-6 col-12">
                                        <div class="form-group mb-3">
                                            <label for="feature_description" class="form-label">Feature Description <span class="text-danger">*</span></label>
                                            <textarea id="feature_description" name="feature_description" class="form-control" rows="3"></textarea>
                                            <div class="d-flex justify-content-between mt-1">
                                                <div class="text-danger small" v-if="form.errors.feature_description">{{ form.errors.feature_description }}</div>
                                                <small :class="wordCount > 450 ? (wordCount >= 500 ? 'text-danger' : 'text-warning') : 'text-muted'">
                                                    {{ wordCount }}/500 kata
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-lg-6 col-12">
                                        <div class="form-group mb-3">
                                            <label for="feature_artikel" class="form-label">Feature Artikel <span class="text-danger">*</span></label>
                                            <textarea id="feature_artikel" name="feature_artikel" class="form-control" rows="8"></textarea>
                                            <small class="text-muted">Artikel lengkap tentang feature. Dapat menggunakan formatting HTML.</small>
                                            <div class="text-danger small" v-if="form.errors.feature_artikel">{{ form.errors.feature_artikel }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button type="button" v-if="isEditing" @click="resetForm" class="btn btn-warning me-2"><i class="ti-trash me-1"></i> Batal</button>
                                <button type="button" @click="submitForm" class="btn btn-primary" :disabled="form.processing">
                                    <span v-if="form.processing" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ti-save-alt me-1"></i> Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            
            <!-- Table Section - Full Width Below Form -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Feature</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center">#</th>
                                            <th>Ikon</th>
                                            <th>Judul</th>
                                            <th class="text-center">Deskripsi</th>
                                            <th class="text-center">Artikel</th>
                                            <th class="text-center">Status</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="featureList.length === 0">
                                            <td colspan="7" class="text-center text-muted">
                                                <i class="fa fa-info-circle me-2"></i>Belum ada feature.
                                            </td>
                                        </tr>
                                        <tr v-for="(item, index) in featureList" :key="item.id">
                                            <td class="text-center align-middle">{{ index + 1 }}</td>
                                            <td class="align-middle">
                                                <span v-if="item.feature_icon" class="d-inline-block bg-white rounded-circle shadow-sm p-2">
                                                    <i :class="item.feature_icon + ' fa-2x text-primary'"></i>
                                                </span>
                                                <span v-else class="text-muted small">No Icon</span>
                                            </td>
                                            <td class="align-middle">
                                                <div class="fw-bold mb-1">{{ item.feature_title }}</div>
                                                <div class="text-muted small">{{ item.feature_subtitle }}</div>
                                            </td>
                                            <td class="text-center align-middle">
                                                <button @click="openDescModal(item)" class="btn btn-outline-success btn-sm">
                                                    <i class="fa fa-info-circle me-1"></i>Lihat Deskripsi
                                                </button>
                                            </td>
                                            <td class="text-center align-middle">
                                                <button @click="openArtikelModal(item)" class="btn btn-outline-warning btn-sm">
                                                    <i class="fa fa-file-text-o me-1"></i>Lihat Artikel
                                                </button>
                                            </td>
                                            <td class="text-center align-middle">
                                                <button 
                                                    @click="toggleStatus(item)" 
                                                    class="btn btn-sm rounded-1 px-3" 
                                                    :class="item.feature_active ? 'btn-success' : 'btn-danger'"
                                                    :disabled="togglingId === item.id"
                                                    title="Klik untuk mengubah status">
                                                    <span v-if="togglingId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else :class="['fa me-1', item.feature_active ? 'fa-check-circle' : 'fa-times-circle']"></i>
                                                    {{ item.feature_active ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                            </td>
                                            <td class="text-center align-middle">
                                                <button @click="editFeature(item)" class="btn btn-outline-primary btn-sm" title="Ubah">
                                                    <i class="fa fa-edit me-1"></i>Edit
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Notification Modal (Error/Warning/Success) -->
            <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header text-white" 
                            :class="{
                                'bg-danger': modalType === 'error',
                                'bg-warning text-dark': modalType === 'warning',
                                'bg-success': modalType === 'success',
                                'bg-info': modalType === 'info'
                            }">
                            <h5 class="modal-title">
                                <i class="me-2" :class="{
                                    'ti-alert': modalType === 'error',
                                    'fa fa-exclamation-triangle': modalType === 'warning',
                                    'fa fa-check-circle': modalType === 'success',
                                    'fa fa-info-circle': modalType === 'info'
                                }"></i>
                                {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                            </h5>
                            <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" 
                                    :class="{
                                        'list-group-item-danger': modalType === 'error',
                                        'list-group-item-warning': modalType === 'warning',
                                        'list-group-item-success': modalType === 'success',
                                        'list-group-item-info': modalType === 'info'
                                    }" 
                                    v-for="(msg, index) in errorModalContent" :key="index">
                                    <i class="fa me-2" :class="{
                                        'fa-exclamation-circle': modalType === 'error',
                                        'fa-exclamation-triangle': modalType === 'warning',
                                        'fa-check-circle': modalType === 'success',
                                        'fa-info-circle': modalType === 'info'
                                    }"></i>{{ msg }}
                                </li>
                            </ul>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn" 
                                :class="{
                                    'btn-danger': modalType === 'error',
                                    'btn-warning': modalType === 'warning',
                                    'btn-success': modalType === 'success',
                                    'btn-info': modalType === 'info'
                                }" 
                                data-bs-dismiss="modal">
                                <i class="fa fa-check-square-o me-2"></i>Mengerti
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- View Description Modal -->
            <div class="modal fade" id="viewDescModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header bg-success text-white">
                            <h5 class="modal-title"><i class="fa fa-info-circle me-2"></i>{{ viewModalTitle }}</h5>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <!-- Feature Info -->
                            <div class="list-group-item list-group-item-info p-3 rounded mb-3">
                                <table class="table table-sm table-borderless mb-0">
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3" style="width: 1%; white-space: nowrap;">Title</td>
                                        <td class="text-center" style="width: 1%;">:</td>
                                        <td class="fw-bold text-primary">{{ viewModalItem.feature_title }}</td>
                                    </tr>
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3">Subtitle</td>
                                        <td class="text-center">:</td>
                                        <td>{{ viewModalItem.feature_subtitle || '-' }}</td>
                                    </tr>
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3">Icon</td>
                                        <td class="text-center">:</td>
                                        <td>
                                            <span v-if="viewModalItem.feature_icon">
                                                <i :class="viewModalItem.feature_icon + ' me-2'"></i>{{ viewModalItem.feature_icon }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            
                            <h5 class="text-success border-bottom pb-2 mb-3"><i class="fa fa-file-text me-2"></i>Konten Deskripsi</h5>
                            <div v-if="viewModalContent" v-html="viewModalContent"></div>
                            <div v-else class="alert alert-warning"><i class="fa fa-exclamation-triangle me-2"></i>Deskripsi belum diisi.</div>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Tutup</button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- View Artikel Modal -->
            <div class="modal fade" id="viewArtikelModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header bg-primary text-white">
                            <h5 class="modal-title"><i class="fa fa-file-text-o me-2"></i>{{ viewModalTitle }}</h5>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <!-- Feature Info -->
                            <div class="list-group-item list-group-item-info p-3 rounded mb-3">
                                <table class="table table-sm table-borderless mb-0">
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3" style="width: 1%; white-space: nowrap;">Title</td>
                                        <td class="text-center" style="width: 1%;">:</td>
                                        <td class="fw-bold text-primary">{{ viewModalItem.feature_title }}</td>
                                    </tr>
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Subtitle</td>
                                        <td class="text-center">:</td>
                                        <td>{{ viewModalItem.feature_subtitle || '-' }}</td>
                                    </tr>
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Title</td>
                                        <td class="text-center">:</td>
                                        <td>{{ viewModalItem.meta_title || '-' }}</td>
                                    </tr>
                                    <tr class="align-top">
                                        <td class="fw-bold text-dark pe-3" style="white-space: nowrap;">Meta Desc</td>
                                        <td class="text-center">:</td>
                                        <td>{{ viewModalItem.meta_description || '-' }}</td>
                                    </tr>
                                </table>
                            </div>
                            
                            <h5 class="text-primary border-bottom pb-2 mb-3"><i class="ti-file me-2"></i>Konten Artikel</h5>
                            <div v-if="viewModalContent" v-html="viewModalContent"></div>
                            <div v-else class="alert alert-warning"><i class="fa fa-exclamation-triangle me-2"></i>Artikel belum diisi.</div>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Tutup</button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Guide Modal -->
            <div class="modal fade" id="featureGuideModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header bg-info text-white">
                            <h5 class="modal-title"><i class="fa fa-book me-2"></i>Panduan Pengisian Feature</h5>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <div class="alert alert-info">
                                <i class="fa fa-info-circle me-2"></i>
                                <strong>Tips:</strong> Ikuti panduan ini untuk mengisi data feature dengan optimal
                            </div>
                            
                            <h6>🎯 Informasi Umum Feature</h6>
                            <ul>
                                <li><strong>Feature Title:</strong> Judul utama feature (maksimal 50 karakter)</li>
                                <li><strong>Feature Subtitle:</strong> Deskripsi singkat yang mendukung judul (maksimal 100 karakter)</li>
                                <li><strong>Feature Description:</strong> Penjelasan singkat <span class="badge bg-warning text-dark">maksimal 500 kata</span></li>
                                <li><strong>Feature Artikel:</strong> Konten lengkap dan detail tentang feature</li>
                            </ul>
                            
                            <h6>🎨 Visual Elements</h6>
                            <ul>
                                <li><strong>Feature Icon:</strong> Gunakan icon FontAwesome (contoh: <code>fa fa-star</code>, <code>fa fa-shield</code>)</li>
                            </ul>
                            
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="alert alert-success">
                                        <h6><i class="fa fa-check-circle me-2"></i>Do's</h6>
                                        <ul class="mb-0 small">
                                            <li>Gunakan bahasa yang mudah dipahami</li>
                                            <li>Fokus pada manfaat untuk user</li>
                                            <li>Gunakan kata-kata yang menarik</li>
                                            <li>Test di mobile view</li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="alert alert-danger">
                                        <h6><i class="fa fa-times-circle me-2"></i>Don'ts</h6>
                                        <ul class="mb-0 small">
                                            <li>Jangan gunakan jargon teknis</li>
                                            <li>Jangan buat teks terlalu panjang</li>
                                            <li>Jangan lupa isi field wajib</li>
                                            <li>Jangan abaikan subtitle</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Tutup</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
.status-feature {
    transition: all 0.2s ease;
    border-radius: 6px;
    min-width: 32px;
}
.status-feature:hover {
    transform: scale(1.05);
}
</style>
